/**
 * app-routes.ts — the child-route table for the ONE `/app` route object.
 *
 * Plan: `.plan/features/2026-07-19-recipe-switch-performance.md` Phase C
 * (route unification).
 *
 * ── What this makes structural ─────────────────────────────────────────────
 * Every `/app*` destination — the dashboard, the library grid, the recipe
 * workspace and its sub-mode segments, the tray destinations (Feed, Discover,
 * Cooks, Groups, Custom logic, Syntax help) and the unknown-path fallback — is
 * a CHILD of a single `/app` route object whose element is `<AppRoute />`. One
 * route object means one AppRoute instance by construction, so crossing from
 * the library to a recipe (and back) is a state switch: the recipe identity,
 * the warm sync connection, the editor and the evaluator worker all survive.
 *
 * ── Measured correction to the Phase C diagnosis (2026-07-24) ──────────────
 * The plan attributed the library↔recipe cost to a REMOUNT caused by `/app`
 * and `/app/r/:slug` being sibling top-level route objects. That premise does
 * not hold. React reconciles by element type at a position, and React Router
 * renders every top-level match at the same depth, so two sibling route
 * objects that both render a bare `<AppRoute />` reuse the same instance. A
 * mount-count probe against the PRE-change table recorded exactly ONE mount
 * across `library → A → library → B` (both for a bare probe element and for
 * the real AppRoute, whose Workspace subtree never remounted). There was no
 * remount to remove, and this change claims no switch-latency win.
 *
 * It is still worth landing, because that single-instance property was
 * INCIDENTAL. The same probe, with ONE of the three sibling objects wrapped in
 * a `<Suspense>` / provider / layout element — the ordinary next edit — went to
 * 4 mounts and 3 unmounts across the same walk: a full teardown of the editor,
 * the sync connection and the evaluator worker on every crossing, with no test
 * to catch it. One route object makes the property hold by construction, and
 * `app.route-unification.test.tsx` pins it.
 *
 * ── The children carry no element ──────────────────────────────────────────
 * `AppRoute` picks its surface from `deriveAppView(pathname)` and the
 * route-derived booleans in `app.tsx`; it renders no `<Outlet/>`. The children
 * exist so those URLs MATCH this branch rather than falling through to another
 * element position. Each declares `element: null` purely to silence React
 * Router's dev-only "Matched leaf route … does not have an element" warning
 * (the check tests `element !== undefined`); `null` renders nothing.
 *
 * Dynamic segments still reach the parent: `matchRouteBranch` accumulates
 * params into ONE object that every match in the branch shares by reference,
 * so `useParams()` inside the `/app` element reads `:slug` from `r/:slug` and
 * `:groupUuid` from `g/:groupUuid`. Verified by probe before the move and
 * pinned by `app.route-unification.test.tsx`.
 */

import type { RouteObject } from "react-router";

/**
 * Children of the single `/app` route object.
 *
 * Exported (rather than inlined into `entry.client.tsx`, which bootstraps the
 * app on import and so cannot be imported by a test) so the unit tests build
 * their routers from the REAL table. A test that hand-rolls its own route shape
 * cannot prove a property of the table the app actually ships.
 */
export const APP_ROUTE_CHILDREN: RouteObject[] = [
  // `/app/library` — the full library grid. The bare `/app` parent is the
  // dashboard (dashboard/library split, 2026-07-11); AppRoute switches on
  // `libraryRouteActive`.
  { path: "library", element: null },
  // `/app/feed` — the Feed (B-WP11b). Rendered in-shell by WorkspaceContainer.
  { path: "feed", element: null },
  // `/app/discover` — Discover (B-WP9), the signed-in entry. The public
  // `/discover` page is a separate top-level route.
  { path: "discover", element: null },
  // `/app/ledger` — the Cooks ledger (A-WP9). Rendered in-shell.
  { path: "ledger", element: null },
  // `/app/g` — Groups index; `/app/g/:groupUuid` — group detail;
  // `/app/g/:groupUuid/r/:shareUuid` — a group-shared recipe. All in-shell.
  { path: "g", element: null },
  { path: "g/:groupUuid", element: null },
  { path: "g/:groupUuid/r/:shareUuid", element: null },
  // `/app/settings/custom-logic` — the .cookrc.yml editor. In-shell.
  { path: "settings/custom-logic", element: null },
  // `/app/help` — the syntax reference. In-shell.
  { path: "help", element: null },
  // `/app/r/:slug` — the recipe workspace, with the sub-mode segments nested
  // one level deeper (two-mode routing, #194 / #187):
  //
  //   /app/r/:slug        — Cook view (default)
  //   /app/r/:slug/edit   — Editor (TipTap, mobile insert bar)
  //
  // The sub-modes were already children of a single parent before this change
  // (P0 remount fix, 2026-06-15) — that fix is preserved verbatim here, one
  // level deeper. Workspace reads the mode from the path
  // (`pathSubModeSegment` + `subModeFromPathSegment`), never from an Outlet.
  //
  // The stale segments (diff / variations / history) stay registered so old
  // bookmarks resolve to Cook mode instead of the NotFound page;
  // `subModeFromPathSegment` maps them to Cook.
  // Wishlist: `.plan/wishlist/2026-05-12-read-mode-subviews.md`
  {
    path: "r/:slug",
    element: null,
    children: [
      { path: "edit", element: null },
      { path: "diff", element: null },
      { path: "variations", element: null },
      { path: "history", element: null },
    ],
  },
  // Any other `/app/**` path — previously a top-level `/app/*` route object
  // with its own `<AppRoute />` element. `deriveAppView` falls back to the
  // dashboard for anything unrecognised, so an unknown `/app` path still lands
  // inside the shell (and `useParams()["*"]` reads the same value it did as a
  // top-level splat).
  //
  // `/app/join/:token` and `/app/friend-invite/:token` stay STANDALONE
  // top-level routes: they are pre-auth landings that resolve a token without
  // the app shell. React Router ranks branches by specificity rather than
  // declaration order, so their static segments beat this splat — pinned by
  // `app.route-unification.test.tsx`.
  { path: "*", element: null },
];
